import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import HistoryIcon from "@mui/icons-material/History";
import ErrorIcon from "@mui/icons-material/Error";
import useJoinGroup from "./useJoinGroup";

import useActiveContexts from "../useActiveContexts";

function ErrorHelperText({ message }) {
  const theme = useTheme();
  return (
    <span style={{ display: "flex", gap: 6, color: theme.palette.error.dark }}>
      <ErrorIcon fontSize="small" />
      <Typography variant="body2">{message}</Typography>
    </span>
  );
}

export default function JoinGroupSearch({ handleClose }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { contexts } = useActiveContexts();
  const [value, setValue] = useState(null); // selected autocomplete value
  const [inputValue, setInputValue] = useState(""); // freesolo text input

  const { rejoinContextMutation, addNewContextMutation, isError, error } =
    useJoinGroup();

  // submit the form
  const handleJoin = () => {
    if (value?.id) {
      rejoinContextMutation.mutate(value);
    } else {
      addNewContextMutation.mutate(inputValue);
    }
  };

  useEffect(() => {
    if (
      rejoinContextMutation?.data?.data?.status > 0 ||
      addNewContextMutation?.data?.data?.status > 0
    )
      handleClose(); // close the dialog if the mutation was successful
  }, [rejoinContextMutation]);

  return (
    <div style={{ display: "flex", gap: 16, minWidth: 400 }}>
      <Autocomplete
        style={{ width: "100%" }}
        freeSolo
        options={contexts.filter(
          (context) => context.community && context.left
        )}
        getOptionLabel={(option) => (option?.name ? option.name : option)}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder={t("library.joinDialog.placeholder")}
            error={error.value}
            helperText={isError ? <ErrorHelperText message={error} /> : null}
            onKeyDown={(e) => {
              if (e.code === "Enter" && inputValue !== "") {
                handleJoin(); // trigger submit on enter
              }
            }}
          />
        )}
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
        }}
        inputValue={inputValue} // free solo input
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <HistoryIcon sx={{ marginRight: 2, color: "text.secondary" }} />
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
      />
      <Button
        color="primary"
        disabled={inputValue === ""}
        style={{ minWidth: 100 }}
        onClick={handleJoin}
      >
        {t("library.joinDialog.confirm")}
      </Button>
    </div>
  );
}
